import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  ContractAgreementService,
  MIME_TO_EXTENSION,
  QUERY_LIMIT,
  TransferProcessService
} from "../../../management-api-client";
import { asyncScheduler, forkJoin, Observable, scheduled } from "rxjs";
import { ContractAgreement, TransferProcessInput, IdResponse, TransferProcess } from "../../../management-api-client/model";
import { ContractOffer } from "../../models/contract-offer";
import { filter, first, map, switchMap, tap } from "rxjs/operators";
import { CatalogBrowserTransferDialog } from "../catalog-browser-transfer-dialog/catalog-browser-transfer-dialog.component";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { TransferProcessStates } from "../../models/transfer-process-states";
import { AppConfigService } from '../../../app/app-config.service';
import { DATASET_CONTEXT, METADATA_CONTEXT, STORAGE_TYPE } from 'src/modules/app/variables';
import { EdrService } from 'src/modules/management-api-client/api/edr.service';
import { PublicService } from 'src/modules/management-api-client/api/public.service';
import { EdcConnectorClientContext } from '@think-it-labs/edc-connector-client';
import { SorterService, CatalogBrowserService, NotificationService, UtilService } from '../../services';
import { MetadataDisplayComponent } from '../common/metadata-display/metadata-display.component';
import { PageEvent } from '@angular/material/paginator';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { UnauthorizedStateService } from 'src/modules/app/auth/unauthorized-state.service';

interface RunningTransferProcess {
  processId: string;
  contractId: string;
  state: TransferProcessStates;
  storageType: string;
  proxyDataAddressOptions: any;
  isTransferStarted: boolean;
}

interface ContractAgreementWithOfferData extends ContractAgreement {
  contractOffer: ContractOffer | null;
}

@Component({
  selector: 'app-contract-viewer',
  templateUrl: './contract-viewer.component.html',
  styleUrls: ['./contract-viewer.component.scss']
})

export class ContractViewerComponent implements OnInit, OnDestroy {
  paginationState = {
    filteredList: [] as ContractAgreementWithOfferData[],
    pagedList: [] as ContractAgreementWithOfferData[],
    pageIndex: 0,
    pageSize: 20
  };
  allContracts: ContractAgreementWithOfferData[] = [];
  searchText = '';
  isUnauthorized = false;
  private runningTransfers: RunningTransferProcess[] = [];
  private pollingHandleTransfer?: any;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private contractAgreementService: ContractAgreementService,
    private edrService: EdrService,
    private publicService: PublicService,
    public dialog: MatDialog,
    private readonly metadataViewDialog: MatDialog,
    private transferService: TransferProcessService,
    private catalogService: CatalogBrowserService,
    private router: Router,
    private notificationService: NotificationService,
    private appConfigService: AppConfigService,
    private sorterService: SorterService,
    private readonly cdref: ChangeDetectorRef,
    public readonly utilService: UtilService,
    private readonly unauthorizedState: UnauthorizedStateService,
    @Inject('HOME_CONNECTOR_STORAGE_ACCOUNT') private homeConnectorStorageAccount: string,
  ) { }

  private static isFinishedState(state: string): boolean {
    return [
      "STARTED",
      "COMPLETED",
      "ERROR",
      "ENDED"].includes(state);
  }

  loadContractAgreements() {
    this.contractAgreementService.queryAllAgreements({
      limit: QUERY_LIMIT,
      offset: 0,
    }).pipe(
      switchMap(contracts => {
        return this.loadContractsWithAssets( contracts )
      })
    ).subscribe({
      next: contracts => {
        this.allContracts = contracts.sort((a, b) => {
          // Sort by contractSigningDate (descending)
          const dateA = a.contractSigningDate || 0;
          const dateB = b.contractSigningDate || 0;
          if (dateA !== dateB) {
            return dateB - dateA; // Newest first
          }
          return this.sorterService.naturalSort(a.assetId || '', b.assetId || '');
        });
        this.utilService.applyFilterAndPagination(
          [...this.allContracts],
          this.filterContracts.bind(this),
          this.searchText,
          this.paginationState
        );
      },
      error: (err: HttpErrorResponse) => {
        if (this.unauthorizedState.isUnauthorized('management')) {
          return;
        }
        this.notificationService.showError('Failed to load contracts');
        console.error(err);
      }
    });
  }

  ngOnInit(): void {
    this.unauthorizedState
      .isUnauthorized$('management')
      .pipe(takeUntil(this.destroy$))
      .subscribe((isUnauthorized) => {
        this.isUnauthorized = isUnauthorized;
        if (isUnauthorized) {
          this.allContracts = [];
          this.paginationState.filteredList = [];
          this.paginationState.pagedList = [];
        }
      });

    this.loadContractAgreements();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  filterContracts(mainList: ContractAgreementWithOfferData[]): ContractAgreementWithOfferData[] {
    return mainList.filter(contractOffer =>
      contractOffer.id.toLowerCase().includes(this.searchText.toLowerCase()) ||
      contractOffer.assetId.toLowerCase().includes(this.searchText.toLowerCase()) ||
      contractOffer.providerId.toLowerCase().includes(this.searchText.toLowerCase()) ||
      contractOffer.contractOffer?.properties.baseUrl?.toLowerCase().includes(this.searchText.toLowerCase()) ||
      this.utilService.searchThroughMetadata(this.findMetadataForAsset(contractOffer.contractOffer!), this.searchText)
    );
  }

  onSearch() {
    this.paginationState.pageIndex = 0;
    this.utilService.applyFilterAndPagination(
      [...this.allContracts],
      this.filterContracts.bind(this),
      this.searchText,
      this.paginationState
    );
  }

  onPageChange(event: PageEvent) {
    this.utilService.onPageChange(
      event, 
      [...this.allContracts],
      this.filterContracts.bind(this),
      this.searchText,
      this.paginationState
    );
  }

  loadContractsWithAssets(contracts: ContractAgreement[]): Observable<ContractAgreementWithOfferData[]> {
    return this.getAllContractOffers().pipe(
      switchMap((allOffers: ContractOffer[]) => {
        const contractObservables: Observable<ContractAgreementWithOfferData>[] = contracts.map(contract => {
          const offerData = this.getContractOfferForAssetId(contract.assetId, allOffers);
          const contractWithOffer = contract as ContractAgreementWithOfferData;
          contractWithOffer.contractOffer = offerData;
          // Wrap the result in an observable
          return scheduled([contractWithOffer], asyncScheduler);
        });
  
        // Combine all observables
        return forkJoin(contractObservables).pipe(
          map(results => results.filter(item => item !== null)));
      })
    );
  }

  asDate(epochSeconds?: number): string {
    if(epochSeconds){
      const d = new Date(0);
      d.setUTCSeconds(epochSeconds);
      return d.toLocaleString();
    }
    return '';
  }

  onTransferClicked(contract: ContractAgreementWithOfferData) {

    const offer = contract.contractOffer!;

    const dialogRef = this.dialog.open(CatalogBrowserTransferDialog, {
      data: {
        isProxyPath: offer.properties.proxyPath ?? false,
        isProxyQueryParams: offer.properties.proxyQueryParams ?? false,
      }
    });

    dialogRef.afterClosed().pipe(first()).subscribe(result => {
      if (result === undefined || result.storageTypeId === undefined || result.storageTypeId === "") {
        return;
      }

      const storageTypeId: string = result.storageTypeId;
      const proxyDataAddressOptions: any = {
        proxyPath: result.proxyUrlPath,
        proxyQueryParams: result.proxyQueryParams
      }

      this.createTransferRequest(contract, storageTypeId, proxyDataAddressOptions)
        .pipe(switchMap(trq => this.transferService.initiateTransfer(trq)))
        .subscribe(transferId => {
          this.startPolling(transferId, contract.id ?? contract["@id"]!, storageTypeId, proxyDataAddressOptions);
        }, error => {
          console.error(error);
          this.notificationService.showError("Error initiating transfer");
        });
    });
  }

  isTransferInProgress(contractId: string): boolean {
    return !!this.runningTransfers.find(rt => rt.contractId === contractId);
  }

  private createTransferRequest(contract: ContractAgreement, storageTypeId: string, proxyDataAddressOptions: any): Observable<TransferProcessInput> {
    return this.getContractOfferForAssetIdAsync(contract.assetId!).pipe(map(contractOffer => {
      const backendUrl = this.appConfigService.getConfig()?.backendUrl;

      const callbackAddresses = [];

      if (storageTypeId == STORAGE_TYPE) {
        callbackAddresses.push(
          {
            "@type": "CallbackAddress",
            "transactional": false,
            "events": [
              "transfer.process.started"
            ],
            "uri": this.getCallbackUrl(backendUrl!, proxyDataAddressOptions)
          }
        )
      }

      const counterPartyAddress = contractOffer?.originator.includes('2025-1') ? 
                            contractOffer?.originator : 
                            `${contractOffer?.originator}/2025-1`;

      const iniateTransfer : any = {
        '@context': ['https://w3id.org/edc/connector/management/v2'],
        '@type': 'TransferRequest',
        counterPartyAddress: counterPartyAddress,
        contractId: contract.id ?? contract["@id"]!,
        transferType: "HttpData-PULL",
            protocol: 'dataspace-protocol-http:2025-1',
        callbackAddresses: callbackAddresses
      };

      return iniateTransfer;
    }));

  }

  /**
   * This method is used to obtain that URL of the connector that is offering a particular asset from the catalog.
   * This is a bit of a hack, because currently there is no "clean" way to get the counter-party's URL for a ContractAgreement.
   *
   * @param assetId Asset ID of the asset that is associated with the contract.
   */
  private getContractOfferForAssetIdAsync(assetId: string): Observable<ContractOffer> {
    return this.catalogService.getContractOffers()
      .pipe(
        map(offers => {
          return offers.find(o => o.assetId === assetId);}
        ),
        map(o => {
          if (o) return o;
          else throw new Error(`No offer found for asset ID ${assetId}`);
        }))
  }

  private getAllContractOffers(): Observable<ContractOffer[]> {
    return this.catalogService.getContractOffers();
  }

  private getContractOfferForAssetId(assetId: string, contractOffers: ContractOffer[]): ContractOffer | null {
    const offer = contractOffers.find(o => o.assetId === assetId);
    if (offer) {
      return offer;
    }
    console.log(`No offer found for asset ID ${assetId}`);
    return null;
  }

  private startPolling(transferProcessId: IdResponse, contractId: string, storageType: string, proxyDataAddressOptions: any) {

    // track this transfer process
    this.runningTransfers.push({
      processId: transferProcessId.id!,
      state: TransferProcessStates.REQUESTED,
      contractId: contractId,
      storageType: storageType,
      proxyDataAddressOptions: proxyDataAddressOptions,
      isTransferStarted: false,
    });

    if (!this.pollingHandleTransfer) {
      console.log(proxyDataAddressOptions.proxyPath);
      this.pollingHandleTransfer = setInterval(this.pollRunningTransfers(), 1000);
    }

  }

  private pollRunningTransfers() {
    return () => {
      scheduled(this.runningTransfers, asyncScheduler) //create from array
        .pipe(switchMap(runningTransferProcess => this.catalogService.getTransferProcessesById(runningTransferProcess.processId).pipe(
          map(transferProcess => ({ runningTransferProcess, transferProcess })) // Combine both into an object
          )
        ),
        filter(({ runningTransferProcess, transferProcess }) => 
          ContractViewerComponent.isFinishedState(transferProcess.state!) && !runningTransferProcess.isTransferStarted),
          tap(({ runningTransferProcess, transferProcess }) => {
            try {
              runningTransferProcess.isTransferStarted = true;
              this.processStartedTransfer(
                transferProcess, runningTransferProcess.storageType, runningTransferProcess.proxyDataAddressOptions);
            } catch {
              runningTransferProcess.isTransferStarted = false;
            }
          })
        )
        .subscribe(() => {
        // clear interval if necessary
        if (this.runningTransfers.length === 0) {
          clearInterval(this.pollingHandleTransfer);
          this.pollingHandleTransfer = undefined;
        }
      }, error => this.notificationService.showError(error))
    }
  }

  private processStartedTransfer = (transfer: TransferProcess, storageType: string, proxyDataAddressOptions: any) => {

    if (storageType == STORAGE_TYPE) {
      this.completeTransfer(transfer);
      return;
    }

    this.processStartedLocalTransfer(transfer, proxyDataAddressOptions);
  }

  private processStartedLocalTransfer = async (transfer: TransferProcess, proxyDataAddressOptions: any) => {
    console.log('[Transfer] Starting local transfer for:', transfer);
    try {
      const address = await this.edrService.requestDataAddress(transfer.id).toPromise();
      console.log(address);

      const endpoint = address.endpoint;
      const authCode = address.authorization;
      console.log('[EDR] Received address:', address);
      console.log('[EDR] Received auth code:', authCode);

      const adjustedEndpoint = this.adjustServiceUrl(endpoint);
      const publicEndpoint = this.getUrlWithQueryParams(adjustedEndpoint, proxyDataAddressOptions);
      console.log(publicEndpoint);

      const context = new EdcConnectorClientContext(undefined, {
        public: publicEndpoint
      });
      console.log(context.public);

      const data: Response = await this.publicService.getTransferredData(authCode, context).toPromise();
      console.log(data);

      this.completeTransfer(transfer);
      this.saveFileToDownloads(data, transfer, publicEndpoint);
    } catch (e: any) {
      const message = (e as Error).message;
      console.log(e);
      this.notificationService.showError(message);
    }
  }

  private getFileExtensionFromUrl(urlString: string): string | null {
    try {
      const url = new URL(urlString);
      const pathname = url.pathname;
      const lastDot = pathname.lastIndexOf('.');
      if (lastDot > 0) {
        return pathname.substring(lastDot);
      }
    } catch (e) {
      console.log('[Download] Failed to parse URL for extension:', e);
    }
    return null;
  }

  private determineFileExtension(contentType: string | null, endpointUrl?: string): string {
    // Priority 1: Check MIME_TO_EXTENSION mapping (excluding application/octet-stream)
    if (contentType && contentType !== 'application/octet-stream' && MIME_TO_EXTENSION[contentType]) {
      console.log('[Download] Using extension from Content-Type:', contentType);
      return MIME_TO_EXTENSION[contentType];
    }

    // Priority 2: Try to extract extension from endpoint URL
    if (endpointUrl) {
      const urlExtension = this.getFileExtensionFromUrl(endpointUrl);
      if (urlExtension) {
        console.log('[Download] Using extension from endpoint URL:', urlExtension);
        return urlExtension;
      }
    }

    // Priority 3: Fallback to .json as default
    console.log('[Download] Using default .json extension');
    return '.json';
  }

  private saveFileToDownloads = async (data: Response, transfer: TransferProcess, endpointUrl?: string) => {
    try {
      const contentType = data.headers.get('Content-Type');
      console.log('[Download] Content-Type:', contentType);
      console.log('[Download] Endpoint URL:', endpointUrl);

      const blob = await data.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      
      const extension = this.determineFileExtension(contentType, endpointUrl);
      a.download = `${transfer.assetId}${extension}`;
    
      document.body.appendChild(a);
      a.click();
      URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('[Download] Error:', err);
      this.notificationService.showError('Download failed');
    }
  };
  
  private adjustServiceUrl = (url: string): string => {
    try {
      return new URL(url).toString();
    } catch (error) {
      console.error('Invalid URL provided:', error);
      return url; 
    }
  }

  private completeTransfer = (transferProcess: any) => {
    // remove from in-progress
    this.runningTransfers = this.runningTransfers.filter(rtp => rtp.processId !== transferProcess.id)
    this.notificationService.showInfo(`Transfer [${transferProcess.id}] complete!`, "Show me!", () => {
      this.router.navigate(['/transfer-history'])
    })
  }

  private getUrlWithQueryParams(url: string, proxyDataAddressOptions: any) {
    const proxyPath = proxyDataAddressOptions.proxyPath.replace(/\s/g, "");
    const proxyQueryParams: { key: string; value: string }[] = proxyDataAddressOptions.proxyQueryParams;

    let result = url;
    if (proxyPath != undefined && proxyPath != '') {
      if (proxyPath[0] != '/') {
        result += '/';
      }
      result += `${proxyPath}`;
    }
    if (proxyQueryParams != undefined && proxyQueryParams.length > 0) {
      const proxyQueryParamsStr = proxyQueryParams
        .map(x => `${x.key.replace(/\s/g, "")}=${x.value.replace(/\s/g, "")}`)
        .join("&");
      result += `?${proxyQueryParamsStr}`;
    }

    return result;
  }

  private getCallbackUrl(url: string, proxyDataAddressOptions: any) {
    const callbackUrl = new URL(this.getUrlWithQueryParams(url, proxyDataAddressOptions));
    const connectorId = this.appConfigService.getConfig()?.connectorId || 'consumer';
    callbackUrl.searchParams.set('requester', connectorId);
    return callbackUrl.toString();
  }

  public getContractSigningDate(contract: ContractAgreementWithOfferData) {
    return this.asDate(
      contract["edc:contractSigningDate"] ??
      contract.contractSigningDate);
  }

  onSelect(offer: ContractOffer) {
    const dialogRef = this.metadataViewDialog.open(MetadataDisplayComponent, {
      data: { 
        metadata: this.findMetadataForAsset(offer),
        asset_name: offer.properties.name || offer.assetId,
      },
    });
    dialogRef.afterClosed().subscribe( );
  }

  shouldDisplayMetadata(offer : ContractOffer) : boolean {
    if (!offer) return false;
    const metadata = this.findMetadataForAsset(offer)
    for (var prop in metadata) {
      if(metadata.hasOwnProperty(prop) && metadata[prop] !== null )
        return true;
    }
    return false;
  }

  findMetadataForAsset(offer: ContractOffer) {
    return offer[DATASET_CONTEXT].filter((_asset : any) => {
      return _asset['@id'] === offer.assetId || _asset.id === offer.assetId;
    })?.[0]?.[METADATA_CONTEXT]?.[0] || {};
  }

  ngAfterContentChecked() {
    this.cdref.detectChanges();
  }
}
